import { cn, Spinner, SuspenseBoundary } from '@agently/ui';
import { fetchUser, useAuthState, useUser } from 'auth';
import useBadge from 'hooks/useBadge';
import { Navigate, Outlet, useLocation } from 'react-router';
import { isAssistantPage } from 'isAssistantPage';
import routes from 'routes';

import BottomMenu from 'components/BottomMenu';
import DeferredMount from 'components/DeferredMount';
import { useMount } from 'ahooks';
import { lazy, Suspense } from 'react';
import { lazyWithRetry } from '@agently/utils';

// Overlays and drawers that aren't part of first paint. Imported by module path:
// a dynamic import of the @agently/components barrel would stay in the entry chunk.
const ActivityFeed = lazy(() =>
  import('@agently/components/activity-feed').then((module) => ({
    default: module.ActivityFeed,
  })),
);
const AgentCoachingDialogs = lazyWithRetry(() =>
  import('@agently/components/src/Copilot/AgentCoaching').then((module) => ({
    default: module.AgentCoachingDialogs,
  })),
);
// Mandatory goal gate: only rendered for users who must set a goal, behind a
// blocking fallback. lazyWithRetry retries (or reloads on a stale chunk) and
// otherwise fails into the route error boundary, so the gate can't be bypassed.
const ForceSetupFinGoal = lazyWithRetry(
  () => import('@agently/components/src/FinGoal/ForceSetupFinGoal'),
);

const PushDiagnosticsReporter = lazy(() =>
  import('services/usePushDiagnostics').then((module) => ({
    default: module.PushDiagnosticsReporter,
  })),
);

const Badge = () => {
  useBadge();
  return null;
};

const DeferredAgentCoachingDialogs = ({ enabled }: { enabled: boolean }) => {
  if (!enabled) return null;

  return <AgentCoachingDialogs enabled />;
};

const PostContentTasks = () => {
  const user = useUser();

  useMount(() => {
    if (import.meta.env.PROD) {
      void import('services/pushStartup')
        .then(({ initializePushForUser }) => initializePushForUser(user.id))
        .catch((error) => {
          console.warn('Failed to initialize push notifications:', error);
        });
    }
  });

  return <PushDiagnosticsReporter key={user.id} userId={user.id.toString()} />;
};

const RequireAuth = () => {
  const { isAuth } = useAuthState();
  const location = useLocation();
  const user = useUser();
  if (isAuth !== 'true' || !user) {
    return (
      <Navigate
        state={{ from: location }}
        to={{ pathname: routes.signinPath, search: location.search }}
      />
    );
  }

  // Profile not complete — send to dedicated onboarding flow
  if (!user.birthday) {
    return <Navigate to={routes.onboardingCompleteProfilePath} />;
  }

  const { isSetFc } = user;
  const isOpenForceFc = isSetFc === false;
  // The Assistant page is full-screen: no bottom menu, so no room reserved for it.
  const fullScreen = isAssistantPage(location.pathname);

  return (
    <>
      <DeferredMount phase="background">
        <Badge />
      </DeferredMount>
      {/* While the goal gate is up (incl. while its chunk loads), keep the shell
          out of focus/keyboard/AT reach, like the modal Drawer does once open. */}
      <div inert={isOpenForceFc}>
        <div
          className={cn(
            'safe-area-x safe-area-top',
            !fullScreen && 'px-4',
            location.pathname !== routes.chatPath && !fullScreen && 'root-b',
            location.pathname === routes.resourcesPath && 'bg-zinc-50',
          )}
        >
          <Suspense
            fallback={
              <div className="grid place-content-center p-2">
                <Spinner />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </div>
        <BottomMenu />
      </div>
      {/* Activity notifications render outside the inert shell; hold them back
          while the goal gate is up so nothing focusable sits beside it. */}
      {!isOpenForceFc && (
        <DeferredMount phase="background">
          <SuspenseBoundary fallback={undefined}>
            <ActivityFeed />
          </SuspenseBoundary>
        </DeferredMount>
      )}
      <DeferredMount phase="third-party">
        <SuspenseBoundary fallback={undefined}>
          <PostContentTasks />
        </SuspenseBoundary>
      </DeferredMount>
      {isOpenForceFc && (
        <Suspense
          fallback={
            <div className="fixed inset-0 z-50 grid place-content-center bg-background/80">
              <Spinner />
            </div>
          }
        >
          <ForceSetupFinGoal onSuccess={fetchUser} isOpen />
        </Suspense>
      )}
      <SuspenseBoundary fallback={undefined}>
        <DeferredAgentCoachingDialogs enabled={!isOpenForceFc} />
      </SuspenseBoundary>
    </>
  );
};

export default RequireAuth;
